import { userFacingError } from "../../authorization";
import type { ShareableScopeKind, ScopeKind } from "../permissions";
import type { BuiltinRoleId } from "../roles";
import type { GrantInput } from "./grantShape";
import {
  assertBuiltinRoleScope,
  assertCustomRoleScope,
  validateGrantsInput,
} from "./grantShape";

type ResolvedRole = {
  id: string;
  scopeKind: string;
};

export type ResolvedCustomRole = ResolvedRole & {
  rolePermissions: ReadonlyArray<{ permission: string }>;
};

type RoleInput = {
  builtinRoleId?: string | null;
  customRoleId?: string | null;
};

export type ResolvedGrantRole<T extends ResolvedRole = ResolvedCustomRole> =
  | {
      builtinRoleId: BuiltinRoleId;
      customRole: null;
    }
  | {
      builtinRoleId: null;
      customRole: T;
    };

export type ResolvedGrant<
  TInput extends RoleInput = GrantInput,
  TRole extends ResolvedRole = ResolvedCustomRole,
> = {
  input: TInput;
  role: ResolvedGrantRole<TRole>;
};

export type ResolvedGrantScope = {
  kind: ShareableScopeKind;
  isPersonalSpace: boolean;
};

function resolveGrantRole<T extends ResolvedRole>(
  grant: RoleInput,
  scopeKind: ScopeKind,
  customRoles: ReadonlyMap<string, T>
): ResolvedGrantRole<T> {
  if (grant.builtinRoleId) {
    return {
      builtinRoleId: grant.builtinRoleId as BuiltinRoleId,
      customRole: null,
    };
  }
  const customRole = customRoles.get(grant.customRoleId!);
  if (!customRole) throw userFacingError("Custom role not found");
  assertCustomRoleScope(customRole, scopeKind);
  return { builtinRoleId: null, customRole };
}

export function validateGrants<T extends ResolvedRole>(
  grants: readonly GrantInput[],
  scopeKind: ScopeKind,
  roles: readonly T[]
): ResolvedGrant<GrantInput, T>[] {
  validateGrantsInput(grants, scopeKind);
  const customRoles = new Map(roles.map((role) => [role.id, role]));
  return grants.map((input) => ({
    input,
    role: resolveGrantRole(input, scopeKind, customRoles),
  }));
}

export function validateServiceAccountGrants<
  TInput extends RoleInput & { scopeKind: ScopeKind },
  TRole extends ResolvedRole,
>(
  grants: readonly { input: TInput; scope: ResolvedGrantScope }[],
  roles: readonly TRole[]
): ResolvedGrant<TInput, TRole>[] {
  const customRoles = new Map(roles.map((role) => [role.id, role]));
  return grants.map(({ input, scope }) => {
    if (!!input.builtinRoleId === !!input.customRoleId) {
      throw userFacingError(
        "Each grant must specify exactly one of builtinRoleId or customRoleId"
      );
    }
    if (input.builtinRoleId) {
      assertBuiltinRoleScope(input.builtinRoleId, input.scopeKind);
    }
    const role = resolveGrantRole(input, input.scopeKind, customRoles);
    if (scope.isPersonalSpace) {
      throw userFacingError(
        "A service account cannot be granted access to a personal space"
      );
    }
    return {
      input,
      role,
    };
  });
}
